import { fetchAuthSession } from '@aws-amplify/auth';

import { BACKEND_CONTEXTUAL_HELP_API } from '../env';

// Cliente del backend de ayuda contextual (whisperbi-backend-contextual-help,
// servicio nuevo e independiente -- lee Markdown desde S3, sin partición por
// tenant, cualquier usuario autenticado puede leerlo). Mismo patrón de auth
// (Bearer idToken de Cognito) y manejo de errores que KnowledgeBaseService.js.
const API_BASE = `${BACKEND_CONTEXTUAL_HELP_API}/help`;

async function getAuthHeader() {
  try {
    const idToken = (await fetchAuthSession()).tokens?.idToken?.toString();
    return idToken ? { Authorization: `Bearer ${idToken}` } : {};
  } catch (error) {
    console.error('ContextualHelpService.getAuthHeader: unable to fetch session', error);
    return {};
  }
}

async function request(path, init = {}) {
  const authHeader = await getAuthHeader();
  const headers = {
    Accept: 'application/json',
    ...(init.headers || {}),
    ...authHeader
  };
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  let response;
  try {
    response = await fetch(url, { ...init, headers, mode: 'cors', cache: 'no-cache' });
  } catch (networkError) {
    throw new Error(`No se pudo contactar el servicio de ayuda contextual. ${networkError.message}`);
  }

  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    let message = raw || `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(raw);
      message = parsed.error || parsed.message || message;
    } catch {
      /* texto plano */
    }
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// Cache en memoria por pestaña (no persiste entre recargas) -- el contenido
// de ayuda es global/estático, no hace falta re-pedir el mismo topic dos
// veces en la misma sesión. Separado de la cache con TTL que ya tiene el
// backend (esto evita directamente el round-trip de red).
const _topicCache = new Map();
const _topicsListCache = new Map();
let _modulesCache = null;

function _topicCacheKey(moduleName, topic) {
  return `${moduleName}/${topic}`;
}

const ContextualHelpService = {
  /** `{ module, topic, content, updated_at }` -- lanza si el topic no existe (404). */
  async getTopic(moduleName, topic) {
    if (!moduleName || !topic) {
      throw new Error('module y topic son requeridos');
    }
    const cacheKey = _topicCacheKey(moduleName, topic);
    if (_topicCache.has(cacheKey)) {
      return _topicCache.get(cacheKey);
    }
    const data = await request(`/${encodeURIComponent(moduleName)}/${encodeURIComponent(topic)}`, { method: 'GET' });
    _topicCache.set(cacheKey, data);
    return data;
  },

  /** `{ module, topics: [...] }` */
  async listTopics(moduleName) {
    if (!moduleName) {
      throw new Error('module es requerido');
    }
    if (_topicsListCache.has(moduleName)) {
      return _topicsListCache.get(moduleName);
    }
    const data = await request(`/${encodeURIComponent(moduleName)}`, { method: 'GET' });
    _topicsListCache.set(moduleName, data);
    return data;
  },

  /** `{ modules: [...] }` */
  async listModules() {
    if (_modulesCache) {
      return _modulesCache;
    }
    const data = await request('', { method: 'GET' });
    _modulesCache = data;
    return data;
  }
};

export default ContextualHelpService;
